import React, { useState } from 'react';
import { CheckCircle2, Mail, MapPin, MessageCircle, Send } from 'lucide-react';
import { submitInquiry } from '../../services/inquiryService';

const CONTACT_ITEMS = [
  {
    icon: Mail,
    label: '电子邮箱',
    value: 'roc@myloyal.cn',
    href: 'mailto:roc@myloyal.cn',
  },
  {
    icon: MessageCircle,
    label: '微信',
    value: 'fire-fighter',
  },
  {
    icon: MapPin,
    label: '工作室地址',
    value: '成都市锦江区三色路38号成都传媒集团B座7楼',
  },
];

const INQUIRY_CATEGORIES = [
  '品牌视觉设计',
  '广告与宣传片',
  '电商图片与视频',
  'AI 模型与工作流',
  '其他咨询',
];

const INITIAL_FORM = {
  name: '',
  brand: '',
  email: '',
  phone: '',
  category: INQUIRY_CATEGORIES[0],
  brief: '',
};

const StudioContactSection: React.FC = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof typeof form) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const canSubmit = Boolean(form.name.trim() && form.email.trim() && form.brief.trim());

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await submitInquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        brand: form.brand.trim(),
        phone: form.phone.trim(),
        category: form.category,
        brief: form.brief.trim(),
      });

      if (result.success) {
        setSubmitted(true);
      } else {
        setError(result.error || '提交失败，请稍后再试');
      }
    } catch {
      setError('提交失败，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="studio-contact" id="studio-contact" aria-labelledby="studio-contact-title">
      <div className="studio-contact__intro" data-studio-reveal="left">
        <div>
          <p className="studio-contact__kicker">合作咨询</p>
          <h2 id="studio-contact-title">
            想象入云，
            <span>创意落地。</span>
          </h2>
          <p className="studio-contact__lead">
            告诉我们你想解决的问题、期待的视觉方向与时间计划。团队会梳理需求，并给出可执行的合作建议。
          </p>
          <p className="studio-contact__promise">
            <span aria-hidden="true" />
            24 小时内回复
          </p>
        </div>

        <ul className="studio-contact__list" aria-label="工作室联系方式">
          {CONTACT_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.label}>
                <span className="studio-contact__item-icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="studio-contact__item-label">{item.label}</span>
                {item.href ? (
                  <a href={item.href}>{item.value}</a>
                ) : (
                  <span className="studio-contact__item-value">{item.value}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <form
        className="studio-contact-form"
        onSubmit={handleSubmit}
        aria-label="创意合作咨询表单"
        aria-describedby={error ? 'studio-contact-form-error' : undefined}
        data-studio-reveal="right"
        style={{ '--studio-reveal-delay': '100ms' } as React.CSSProperties}
      >
        {submitted ? (
          <div className="studio-contact-form__done" role="status" aria-live="polite">
            <span aria-hidden="true"><CheckCircle2 /></span>
            <h3>创意简报已收到</h3>
            <p>感谢你的咨询，我们将在 24 小时内通过邮箱或电话与你联系。</p>
          </div>
        ) : (
          <>
            <div className="studio-contact-form__head">
              <h3>项目简报</h3>
              <p><em aria-hidden="true">*</em> 为必填项</p>
            </div>

            <div className="studio-contact-form__row">
              <label className="studio-contact-field">
                <span>姓名 <em aria-hidden="true">*</em></span>
                <input
                  name="name"
                  autoComplete="name"
                  value={form.name}
                  onChange={set('name')}
                  placeholder="请输入姓名"
                  required
                />
              </label>
              <label className="studio-contact-field">
                <span>公司或品牌名称</span>
                <input
                  name="organization"
                  autoComplete="organization"
                  value={form.brand}
                  onChange={set('brand')}
                  placeholder="选填"
                />
              </label>
            </div>

            <div className="studio-contact-form__row">
              <label className="studio-contact-field">
                <span>电子邮箱 <em aria-hidden="true">*</em></span>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="请输入常用邮箱"
                  required
                />
              </label>
              <label className="studio-contact-field">
                <span>联系电话或微信号</span>
                <input
                  name="phone"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="选填，方便快速联系"
                />
              </label>
            </div>

            <label className="studio-contact-field">
              <span>合作类别 <em aria-hidden="true">*</em></span>
              <select name="category" value={form.category} onChange={set('category')}>
                {INQUIRY_CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </label>

            <label className="studio-contact-field">
              <span>项目简报 <em aria-hidden="true">*</em></span>
              <textarea
                name="brief"
                value={form.brief}
                onChange={set('brief')}
                rows={5}
                placeholder="请简单描述项目背景、期望的视觉方向、所需交付物与预期时间。"
                required
              />
            </label>

            {error && (
              <p id="studio-contact-form-error" className="studio-contact-form__error" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="studio-contact-form__submit"
              disabled={!canSubmit || submitting}
              aria-busy={submitting}
            >
              <Send aria-hidden="true" />
              {submitting ? '正在提交…' : '提交创意简报'}
            </button>
          </>
        )}
      </form>
    </section>
  );
};

export default StudioContactSection;
